import React, { FC } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import {
	FormControl,
	FormLabel,
	FormControlLabel,
	Radio,
	Grid,
	Typography,
	FormGroup
} from '@material-ui/core';

import useCommonStyles from 'styles/common';
import CommonButton from 'components/common/CommonButton';
import { VoteFormValues } from 'types';
import { VoteOption, VoteReasonKey, VoteReasonLabel } from 'constants/enums';
import titleRedImg from 'assets/banner/vote-form-title-red.svg';
import redImg from 'assets/banner/vote-form-img-red.png';

import titleWhiteImg from 'assets/banner/vote-form-title-white.svg';
import whiteImg from 'assets/banner/vote-form-img-white.png';
import CenterImage from 'components/common/CenterImage';
import LabeledInput from 'components/common/LabeledInput';


const useStyles = makeStyles((theme) => ({
	content: {
		maxHeight: 'calc(80vh)',
		maxWidth: 'calc(80vw - 288px)',
		overflowY: 'auto',
		backgroundColor: theme.palette.background.paper,
		color: 'var(--color-tertiary)',
		borderRadius: 16,
		padding: '67px 144px',
		textAlign: 'left',
		[theme.breakpoints.down('xs')]: {
			padding: '40px 0',
			width: '90vw',
			maxWidth: 'unset',
		}
	},
	desc: {
		[theme.breakpoints.down('xs')]: {
			padding: '0 24px'
		}
	},
	form: {
		width: 'max-content',
		marginTop: 16,

		[theme.breakpoints.down('xs')]: {
			padding: '0 37px',
			width: '-webkit-fill-available'
		}
	}
}));

interface Props {
	option?: VoteOption;
	onSubmit?: () => void;
	values: VoteFormValues;
	setValueByKey: (key: string, value: any) => void;
}

const VoteModalContent: FC<Props> = ({ option, onSubmit, values, setValueByKey }) => {
	const classes = useStyles();
	const common = useCommonStyles();

	const inputFields: {
			key: Exclude<keyof VoteFormValues, 'option' | 'reason'>;
			label: string;
			placeholder?: string;
			onChange: (val: string) => void;
			onBlur?: (val: string) => void;
			autoComplete?: string;
	}[] = [
			{ key: 'name', 
				label: '姓　名',
				autoComplete: 'name',
				onChange: (val: string) => {
					setValueByKey('name', val);
				}
			},
			{ key: 'phone', 
				label: '電　話',
				autoComplete: 'tel',
				onChange: (val: string) => {
					if (/^[\d\s-+]*$/.test(val)) {
						setValueByKey('phone', val);
					}
				},
			},
			{ key: 'email', 
				label: '電子信箱',
				autoComplete: 'email',
				onChange: (val: string) => {
					setValueByKey('email', val);
				},
			},
			{ key: 'idLast5', 
				label: '身分證後五碼',
				onChange: (val: string) => {
					if (/^\d{0,5}$/.test(val)) {
						setValueByKey('idLast5', val);
					}
				}
			},
	];

	const redVoteReasons = [
		VoteReasonKey.SWEET_TOMATO,
		VoteReasonKey.VERSATILE,
		VoteReasonKey.NO_FAIL,
		VoteReasonKey.WESTERN_STYLE]

	const whiteVoteReasons = [
		VoteReasonKey.HOKKAIDO_MILK,
		VoteReasonKey.RICH_FLAVOR,
		VoteReasonKey.FAMILY_FAVORITE,
		VoteReasonKey.WESTERN_STYLE]

	const isRed = option === VoteOption.RED;
	
	const reasonsList = isRed ? redVoteReasons : whiteVoteReasons;
	const selectedReasons = (values.reason || "").split(",").filter(Boolean);

	const handleReasonToggle = (reason: string) => {
		const updated = selectedReasons.includes(reason)
			? selectedReasons.filter((r) => r !== reason)
			: [...selectedReasons, reason];
		setValueByKey("reason", updated.join(","));
	};

	return (<div className={cx(classes.content, common.columnCenter)}>
		<CenterImage src={isRed ? titleRedImg : titleWhiteImg} maxWidth={288}/>
		<CenterImage src={isRed ? redImg : whiteImg} maxWidth={404} style={{ marginTop: 24}}/>
		<div className={ classes.desc }>

		<Typography variant='body1' align='center' style={{ marginTop: 16}}>{ '投票規則簡易說明' }</Typography>
		<Typography variant='body1' style={{ marginTop: 32}}>{ '每次投票皆須填寫個人基本資料，請填寫正確資訊。' }</Typography>
		<Typography
			variant="body2"
			style={{ whiteSpace: 'pre-line', marginTop: 16 }}
		>
			{`* 每人每日限投一票，可天天投票應援，抽中機會越高 (每人最多限得一個獎品)。
			* 每次需登入有效e-mail進行投票，將以e-mail要求得獎者回覆
			* 不可使用不同e-mail重覆投票。
			* 參加本活動者如未滿18歲(西元2007年6月日以後出生)或未正確填寫資料，主辦單位將取消其參加及兌獎資格。
			* 個人資料僅限台灣好侍食品「2025 Masaの紅白洋食對決」發票登錄及線上投票活動使用。
			* 完整活動辦法詳見活動主頁。`}
		</Typography>
		</div>

		<Grid 
			container spacing={2} 
			direction="column" 
			className={ cx(classes.form) }
		>
			<Grid item>
				<FormControl component="fieldset">
					{ isRed ? (
						<FormLabel component="legend">Q：請問您喜歡蕃茄紅醬的原因？(可複選)</FormLabel>
					) : (
						<FormLabel component="legend">Q：請問您喜歡白醬的原因？(可複選)</FormLabel>
					)}
					<FormGroup>
						{reasonsList.map((reason) => (
							<FormControlLabel
								key={reason}
								control={
									<Radio
										checked={selectedReasons.includes(reason)}
										onClick={() => handleReasonToggle(reason)}
									/>
								}
								label={VoteReasonLabel[reason as VoteReasonKey]}
							/>
						))}
					</FormGroup>
				</FormControl>
			</Grid>

			{inputFields.map(({ key, label, autoComplete, onChange, onBlur }) => (
				<Grid item>
					<LabeledInput
							label={label}
							value={values[key] || ''}
							onChange={onChange}
							onBlur={onBlur}
							color={'var(--color-tertiary)'}
							autoComplete={autoComplete}
					/>
				</Grid>
			))}
		</Grid>

		<div className={cx(common.fullWidth, common.center)} style={{ margin: '36px 0 50px 0'}}>
			<CommonButton
				buttonText="立即投票"
				onClick={onSubmit}
				/>
		</div>
	</div>);
}

export default VoteModalContent;
